/**
 * ThemeProvider — owns the active brand theme, persists the user's choice in
 * localStorage, and re-applies CSS variables whenever the active pack or mode
 * changes.
 *
 * Scope
 * -----
 * This is the *brand* theme provider. It is layered *under* the existing
 * `next-themes`-style `@/components/theme-provider` which manages the
 * `dark` class on <html>. Both can coexist:
 *   - next-themes flips light/dark by toggling a class
 *   - this provider supplies the actual color/typography tokens for that mode
 *
 * Phase 1 persistence is localStorage-only. Phase 2 swaps the source to the
 * Lovable Cloud `tenant_themes` table without touching this API.
 */
import * as React from "react";
import { applyTheme, COLOR_VAR_MAP, CHART_VAR_MAP, resetTheme } from "@/theme/applyTheme";
import { autoCorrectTheme } from "@/theme/contrast";
import { themePackSchema } from "@/theme/schema";
import { DEFAULT_THEME, smplifyDefault } from "@/theme/presets/smplify-default";
import type { ResolvedMode, ThemePack } from "@/theme/types";
import { CopyContext, type CopyContextValue } from "@/theme/copy/CopyContext";
import { loadCopyPrefs, saveCopyPrefs, type CopyPrefs } from "@/theme/copy/prefs";
import {
  filterOverridesForPreset,
  intersectPresets,
  type CopyPresetId,
} from "@/theme/copy/presets";
import {
  loadTenantCopyPreset,
  saveTenantCopyPreset,
} from "@/theme/copy/tenantPreset";

/** localStorage keys — kept in one place so the first-paint script and tests can mirror them. */
export const THEME_CACHE_KEY = "smplify_theme_cache";
export const THEME_PACK_KEY = "smplify_theme_pack";
export const THEME_MODE_KEY = "smplify_theme_mode";

interface ThemeContextValue {
  /** The currently applied theme. Always defined — falls back to default. */
  theme: ThemePack;
  /** Currently active mode (after fallback resolution). */
  mode: ResolvedMode;
  /** Apply a new pack. Runs through `autoCorrectTheme` before persisting. */
  setTheme: (pack: ThemePack) => void;
  /** Switch between light and dark on the current pack. */
  setMode: (mode: ResolvedMode) => void;
  /** Drop overrides and return to the Smplify default. */
  resetToDefault: () => void;
  /**
   * Tenant-level copy preset — the allowlist of which copy keys *any* theme
   * is permitted to rewrite for this tenant. Independent of the per-theme
   * preset; the runtime applies the stricter of the two.
   */
  tenantCopyPreset: CopyPresetId;
  setTenantCopyPreset: (id: CopyPresetId) => void;
  /** The preset actually in effect right now (intersection of theme+tenant). */
  effectiveCopyPreset: CopyPresetId;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

/**
 * Build the precomputed CSS map used by the first-paint inline script in
 * `index.html`. We do this once when caching so the cold-load path doesn't
 * have to import the schema or applier.
 */
function buildCssCache(pack: ThemePack, mode: ResolvedMode): Record<string, string> {
  const tokens = mode === "dark" ? pack.dark : pack.light;
  if (!tokens) return {};
  const out: Record<string, string> = {};
  for (const [token, value] of Object.entries(tokens.colors)) {
    out[COLOR_VAR_MAP[token as keyof typeof COLOR_VAR_MAP]] = value;
  }
  for (const [token, value] of Object.entries(tokens.charts)) {
    out[CHART_VAR_MAP[token as keyof typeof CHART_VAR_MAP]] = value;
  }
  out["--radius"] = pack.surfaces.radius;
  return out;
}

function loadFromStorage(): { pack: ThemePack; mode: ResolvedMode } {
  if (typeof window === "undefined") {
    return { pack: DEFAULT_THEME, mode: "light" };
  }
  try {
    const rawPack = localStorage.getItem(THEME_PACK_KEY);
    const rawMode = localStorage.getItem(THEME_MODE_KEY);
    const mode: ResolvedMode = rawMode === "dark" ? "dark" : "light";
    if (!rawPack) return { pack: DEFAULT_THEME, mode };
    const parsed = themePackSchema.safeParse(JSON.parse(rawPack));
    if (!parsed.success) return { pack: DEFAULT_THEME, mode };
    return { pack: parsed.data as ThemePack, mode };
  } catch {
    return { pack: DEFAULT_THEME, mode: "light" };
  }
}

function persist(pack: ThemePack, mode: ResolvedMode): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(THEME_PACK_KEY, JSON.stringify(pack));
    localStorage.setItem(THEME_MODE_KEY, mode);
    localStorage.setItem(
      THEME_CACHE_KEY,
      JSON.stringify({ slug: pack.meta.slug, mode, css: buildCssCache(pack, mode) }),
    );
  } catch {
    /* storage quota / privacy mode — ignore, theme still works for the session */
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [{ pack, mode }, setState] = React.useState(() => loadFromStorage());
  const [copyPrefs, setCopyPrefsState] = React.useState<CopyPrefs>(() => loadCopyPrefs());
  const [forceRevealAll, setForceRevealAll] = React.useState(false);
  const [tenantCopyPreset, setTenantCopyPresetState] = React.useState<CopyPresetId>(
    () => loadTenantCopyPreset(),
  );

  // Apply on mount and on every change.
  React.useEffect(() => {
    applyTheme(pack, mode);
    persist(pack, mode);
  }, [pack, mode]);

  const setTheme = React.useCallback((next: ThemePack) => {
    const corrected = autoCorrectTheme(next);
    setState((prev) => ({ pack: corrected, mode: prev.mode }));
  }, []);

  const setMode = React.useCallback((next: ResolvedMode) => {
    setState((prev) => ({ pack: prev.pack, mode: next }));
  }, []);

  const resetToDefault = React.useCallback(() => {
    resetTheme();
    setState({ pack: smplifyDefault, mode: "light" });
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem(THEME_PACK_KEY);
        localStorage.removeItem(THEME_MODE_KEY);
        localStorage.removeItem(THEME_CACHE_KEY);
      } catch {
        /* ignore */
      }
    }
  }, []);

  const setCopyPrefs = React.useCallback((next: CopyPrefs) => {
    setCopyPrefsState(next);
    saveCopyPrefs(next);
  }, []);

  const setTenantCopyPreset = React.useCallback((next: CopyPresetId) => {
    setTenantCopyPresetState(next);
    saveTenantCopyPreset(next);
  }, []);

  // The effective preset is the *stricter* of the theme's declared preset
  // (if any) and the tenant's preset. This is what we filter labels against
  // at render time, regardless of what the schema parsed earlier.
  const effectiveCopyPreset = React.useMemo<CopyPresetId>(
    () => intersectPresets(pack.meta.copyPreset, tenantCopyPreset),
    [pack.meta.copyPreset, tenantCopyPreset],
  );

  // Re-filter labels at the boundary. The schema already filtered against the
  // theme's own preset, but the tenant preset may be stricter — and a theme
  // installed before the tenant tightened its preset would otherwise leak
  // disallowed labels through.
  const filteredLabels = React.useMemo(
    () => filterOverridesForPreset(pack.labels, effectiveCopyPreset).allowed,
    [pack.labels, effectiveCopyPreset],
  );

  const value = React.useMemo<ThemeContextValue>(
    () => ({
      theme: pack,
      mode,
      setTheme,
      setMode,
      resetToDefault,
      tenantCopyPreset,
      setTenantCopyPreset,
      effectiveCopyPreset,
    }),
    [
      pack,
      mode,
      setTheme,
      setMode,
      resetToDefault,
      tenantCopyPreset,
      setTenantCopyPreset,
      effectiveCopyPreset,
    ],
  );

  const copyValue = React.useMemo<CopyContextValue>(
    () => ({
      labels: filteredLabels,
      prefs: copyPrefs,
      setPrefs: setCopyPrefs,
      forceRevealAll,
      setForceRevealAll,
    }),
    [filteredLabels, copyPrefs, setCopyPrefs, forceRevealAll],
  );

  return (
    <ThemeContext.Provider value={value}>
      <CopyContext.Provider value={copyValue}>{children}</CopyContext.Provider>
    </ThemeContext.Provider>
  );
}

export function useBrandTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error("useBrandTheme must be used inside <ThemeProvider>");
  return ctx;
}
